/**
 * SkipToContent — WCAG 2.1 AA "skip navigation" link.
 *
 * Renders as the very first focusable element in the DOM so that keyboard
 * and screen-reader users can bypass the sidebar and header and jump
 * directly to the main content area.
 *
 * Usage:
 *   1. Place <SkipToContent /> as the first child of the page root.
 *   2. Add id="main-content" to the <main> / content <Box>.
 *
 * Visibility:
 *   - Off-screen by default (clip + translate).
 *   - Snaps to the top-left corner when it receives keyboard focus so
 *     sighted keyboard users can also see and use it.
 */
import { A11Y, STATUS } from '@/theme/vizColors';

export default function SkipToContent() {
  return (
    <a
      href="#main-content"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 9999,
        padding: '12px 24px',
        background: A11Y.skipToContentBg,
        color: STATUS.white,
        fontSize: '1rem',
        fontWeight: 600,
        borderRadius: '0 0 4px 0',
        textDecoration: 'none',
        // Hidden off-screen until focused
        transform: 'translateY(-100%)',
        transition: 'transform 0.15s ease',
        // Focus ring uses Hi-Vis brand token (WCAG 2.1 AA — 3:1 minimum contrast)
        outline: 'none',
      }}
      onFocus={(e) => {
        const el = e.currentTarget as HTMLAnchorElement;
        el.style.transform = 'translateY(0)';
        el.style.outline = '3px solid var(--color-hi-vis)';
        el.style.outlineOffset = '2px';
      }}
      onBlur={(e) => {
        const el = e.currentTarget as HTMLAnchorElement;
        el.style.transform = 'translateY(-100%)';
        el.style.outline = 'none';
      }}
    >
      Skip to main content
    </a>
  );
}
